// src/components/IntelligenceSection.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { FaRobot, FaChartLine, FaCogs } from 'react-icons/fa';

const features = [
  {
    label: 'GPT Chatbots',
    Icon: FaRobot,
    title: 'Custom GPT Chatbots',
    desc: '24/7 AI assistants tuned to your brand, capturing leads and handling support with human-like fluency.',
  },
  {
    label: 'Analytics',
    Icon: FaChartLine,
    title: 'Predictive Analytics',
    desc: 'Drive decisions with machine-learning forecasts—turn raw data into actionable insights.',
  },
  {
    label: 'Automation',
    Icon: FaCogs,
    title: 'Workflow Automation',
    desc: 'Automate repetitive tasks and trigger AI-driven pipelines—focus on innovation, not routine.',
  },
];

export default function IntelligenceSection() {
  const theme = useTheme();
  const [tab, setTab] = React.useState(0);

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 12 },
        color: 'secondary.contrastText',
        // animated gradient backdrop
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundSize: '400% 400%',
          filter: 'blur(80px)',
          zIndex: -2,
          animation: 'gradientShift 12s ease infinite',
        },
        // animate keyframes
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      {/* floating blobs */}
      {[
        { size: 300, top: -80, left: -100, duration: 10 },
        { size: 200, bottom: -60, right: -80, duration: 14 },
      ].map((b, i) => (
        <motion.div
          key={i}
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ repeat: Infinity, duration: b.duration }}
          style={{
            position: 'absolute',
            width: b.size,
            height: b.size,
            borderRadius: '50%',
            background: theme.palette.primary.light,
            opacity: 0.2,
            top: b.top,
            left: b.left,
            bottom: b.bottom,
            right: b.right,
            zIndex: -1,
          }}
        />
      ))}

      <Container sx={{ position: 'relative', zIndex: 1 }}>
        {/* Title & Intro */}
        <Typography
          variant="h3"
          align="center"
          fontWeight={800}
          gutterBottom
          sx={{ mb: 2 }}
        >
          Lemda Intelligence
        </Typography>
        <Typography
          align="center"
          sx={{
            maxWidth: 600,
            mx: 'auto',
            mb: 4,
            lineHeight: 1.6
          }}
        >
          Our AI-Powered Program for Web & App Development. Leverage GPT chatbots,
          predictive analytics, and end-to-end automation to build smarter,
          more engaging experiences.
        </Typography>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          centered
          textColor="inherit"
          indicatorColor="primary"
          sx={{ mb: 6 }}
        >
          {features.map((f) => (
            <Tab key={f.label} label={f.label} />
          ))}
        </Tabs>

        {/* Feature Panel */}
        {features.map((f, i) => (
          i === tab && (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: { xs: 3, md: 5 },
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  borderRadius: theme.shape.borderRadius,
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mb: 2,
                    p: 1.5,
                    bgcolor: 'primary.light',
                    borderRadius: '50%',
                  }}
                >
                  <f.Icon size={40} style={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {f.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {f.desc}
                </Typography>
              </Paper>
            </motion.div>
          )
        ))}

        {/* Call to Action */}
        <Box textAlign="center" mt={6}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1.5
              }}
            >
              Explore Our AI Solutions
            </Button>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}
